/**
 * Re-renders the Retail Rocket markup blocks when the cart drawer's product list changes.
 *
 * Retail Rocket's script stops scanning the DOM at `DOMContentLoaded`. On Shopify the
 * add-to-cart is an AJAX call, so their block would keep showing recommendations for whatever
 * the cart held on page load. This module watches the drawer's container and forces a render
 * whenever the cart's products change.
 *
 * Loaded as `type="module"` from `snippets/cart-drawer.liquid`, which doubles as the Section
 * Rendering API target: a module with the same URL never re-executes, so the observer is not
 * duplicated on every cart update.
 *
 * @see docs/retail-rocket-bloques-pendientes.md
 */

/**
 * `data-product-id` is the signal, not the container being emptied. Measured on the live theme:
 * the Section Rendering API morph keeps the container node itself untouched and only rewrites
 * attributes, so there is nothing to detect in the container's children. That same morph does
 * rewrite this attribute from Liquid, and it changes if and only if the cart's variant list
 * changed — bumping the quantity of a line already in the cart leaves the recommendations valid
 * and correctly triggers nothing.
 *
 * It carries variant ids: Retail Rocket's product id is the Shopify variant, and the Shopify
 * product id is what they call the group id.
 */
const PRODUCT_IDS_ATTRIBUTE = 'data-product-id';

/**
 * Makes Retail Rocket refill the drawer's container, and only that one.
 *
 * `retailrocket.markup.render()` takes no target, but it skips every container that still
 * carries `initialized`. Clearing it on the drawer's block alone leaves the page carousels
 * untouched: re-rendering them would wipe any state their own markup keeps, such as a size a
 * shopper picked before adding to cart (measured on www.errante85.com, 2026-10-01).
 *
 * No-op while `window.retailrocket` is missing — their script loads asynchronously and a cart
 * update can beat it.
 *
 * @param {Element} block - The drawer's markup block.
 */
function render(block) {
  const retailRocket = window.retailrocket;
  if (!retailRocket?.markup?.render) return;

  block.removeAttribute('initialized');
  delete retailRocket.modules?.duplicates;
  retailRocket.markup.render();
}

const drawer = document.getElementById('cart-drawer');

if (drawer) {
  const blockInDrawer = () => drawer.querySelector('[data-retailrocket-markup-block]');

  // Seeded from the current value so a cart that already had products on page load does not
  // trigger a redundant render — Retail Rocket filled that container on its initial scan.
  // Null while the cart is empty: the container only exists on the non-empty branch, so adding
  // the first product surfaces it through childList.
  let renderedProductIds = blockInDrawer()?.dataset.productId ?? null;

  const observer = new MutationObserver(() => {
    const block = blockInDrawer();
    const productIds = block?.dataset.productId ?? null;
    if (productIds === renderedProductIds) return;

    renderedProductIds = productIds;
    if (block) render(block);
  });

  observer.observe(drawer, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: [PRODUCT_IDS_ATTRIBUTE],
  });
}
